import { occFetch, occUrl } from "~/app/api/occ/occFetch";
import { SearchParamBuilder } from "~/utils/searchParamBuilder";
import {
  type dtoSalesPartnerData,
  type SchaefflerStoreFinderSearchPageWsDTO,
} from "~/domains/salesPartner/types/SalesPartnerTypes";
import { moxyStringMapToRecord } from "~/common/types/MoxyMapDTO";

export enum SearchType {
  Partner = "partner",
  Address = "address",
  CertificateID = "certificateID",
}

export interface InputsData {
  query: string;
  radius: number;
  filterBySalesPartner: boolean;
  searchType: SearchType;
  productCompetence?: string[];
  certifiedCompetence?: string[];
  countryCode: string;
}

export interface SalesPartnerSearchQuery {
  query: string;
  radius: number;
  searchType: SearchType;
  productCompetence: string[];
  certifiedCompetence: string[];
  countryCode: string;
  lat: number | null;
  lng: number | null;
  preferredOnly: boolean;
}

export type UrlParams = Pick<
  InputsData,
  "query" | "radius" | "countryCode" | "searchType" | "productCompetence" | "certifiedCompetence"
> & {
  preferredSalesPartner?: string;
  lat?: number;
  lng?: number;
  salesPartnerId?: string;
};

/**
 * Error class for API errors with HTTP status code
 */
export class SalesPartnerApiError extends Error {
  public readonly status: number;

  constructor(status: number, message?: string) {
    super(message ?? `API error: ${status}`);
    this.name = "SalesPartnerApiError";
    this.status = status;
  }
}

export async function getSalesPartnerSearch(
  inputsData: UrlParams,
  pageNumber?: number,
  abortSignal?: AbortSignal,
): Promise<dtoSalesPartnerData> {
  const url = createUrlFromInputValues(inputsData, pageNumber);
  const response = await occFetch(url, { signal: abortSignal });

  if (!response.ok) {
    throw new SalesPartnerApiError(response.status, `Error status: ${response.status}`);
  }

  const wireData = (await response.json()) as SchaefflerStoreFinderSearchPageWsDTO;
  return {
    ...wireData,
    results: (wireData.results ?? []).map((result) => ({
      ...result,
      features: moxyStringMapToRecord(result.features),
      certifiedCompetences: result.certifiedCompetences ?? [],
      productCompetences: result.productCompetences ?? [],
      certifiedDescriptionList: result.certifiedDescriptionList ?? [],
      manufacturerLogoMap: result.manufacturerLogoMap ? moxyStringMapToRecord(result.manufacturerLogoMap) : undefined,
    })),
  };
}

export function createUrlFromInputValues(inputsData: UrlParams, pageNumber?: number): string {
  const builder = new SearchParamBuilder();

  builder.setIfDefined("searchType", inputsData.searchType);
  builder.setIfDefined("show", "All");
  builder.setIfDefined("q", inputsData.query);

  if (inputsData.searchType === SearchType.Address && inputsData.radius !== -1) {
    builder.setIfDefined("radius", inputsData.radius);
  }

  builder.setIfDefined("latitude", inputsData.lat);
  builder.setIfDefined("longitude", inputsData.lng);

  if (!inputsData.salesPartnerId) {
    builder.setIfDefined("country", inputsData.countryCode);
  }

  builder.setIfDefined("preferredSalesPartner", inputsData.salesPartnerId);
  builder.setIfDefined("page", pageNumber);

  builder.appendArrayParam("productCompetence", inputsData.productCompetence);
  builder.appendArrayParam("certifiedCompetence", inputsData.certifiedCompetence);

  return occUrl(`/store-finder/search?${builder.toString()}`);
}
